import { useState } from 'react';
import type { ChangeEvent } from 'react';
import type { ApolloError } from '@apollo/client';
import { MarketingChannel } from '../codegen/types';
import { useFeatureFlagsContext } from '../context/featureFlagsContext';
import { useLocaleContext } from '../context/localeContext';
import { useStaticContext } from '../context/staticContext';
import { useUpdateCustomerMarketingPreferencesMutation } from '../graphqlDocument/marketingPreferences/updateCustomerMarketingPreferences.generated';
import type { BffError } from '../services/errors';
import type { MarketingCategory, MarketingChoice } from '../types/marketingPreferences';
import { getLanguageLiteralFromLocale } from '../utils';
import { capitalizeFirstLetter } from '../utils/capitalizeFirstLetter';
import { grecaptcha } from '../utils/grecaptcha';
import { sendSubscribeToNewsletterEvent } from '../utils/gtm/events/subscribe_to_newsletter/sendSubscribeToNewsletterEvent';
import { Locale } from '../utils/internationalization/localeHelper';
import { useLoadingCursor } from './useLoadingCursor';

export const useNewsletter = () => {
  const { locale } = useLocaleContext();
  const { marketingPreferences } = useStaticContext();
  const [checkboxError, setCheckboxError] = useState(false);
  const { isAuthRecaptchaEnabled } = useFeatureFlagsContext();
  const [recaptchaError, setRecaptchaError] = useState<string | null>();
  const [languageSelectionError, setLanguageSelectionError] = useState(false);

  const defaultCheckboxesState =
    marketingPreferences?.marketingPreferences &&
    Object.entries(marketingPreferences?.marketingPreferences as MarketingCategory).map(([, item]) => ({
      key: item.key,
      optIn: false,
    }));

  const [marketingChoices, setMarketingChoices] = useState<MarketingChoice>({ ...defaultCheckboxesState });

  const [languageChoice, setLanguageChoice] = useState(
    // if there are multiple languages available we do not set default value to force user to choose language.
    // if only one language is available we set page locale language by default
    marketingPreferences && marketingPreferences?.languages.length > 1 ? '' : getLanguageLiteralFromLocale(locale),
  );
  const [updateCustomerMarketingPreferences, { data: newsletterData, error, called: isCalled, loading }] =
    useUpdateCustomerMarketingPreferencesMutation();

  useLoadingCursor(loading);

  const onSubmit = async ({ email }: { email: string }) => {
    const isCheckboxError = marketingChoices && !Object.entries(marketingChoices).some(([, item]) => item.optIn);
    const isLanguageSelectionError =
      marketingPreferences && marketingPreferences.languages.length > 1 && languageChoice === '';
    setCheckboxError(isCheckboxError);
    setLanguageSelectionError(!!isLanguageSelectionError);
    setRecaptchaError('');
    if (isCheckboxError || isLanguageSelectionError) {
      return;
    }
    let recaptchaToken;
    if (isAuthRecaptchaEnabled) {
      recaptchaToken = await grecaptcha();
    }
    const preferences = Object.entries(marketingChoices).map(([, item]) => item);

    try {
      await updateCustomerMarketingPreferences({
        variables: {
          input: {
            email,
            preferences,
            locale,
            channel: locale === Locale.STOREFRONT_OUTLET_LOCALE ? MarketingChannel.OUTLET : MarketingChannel.FULLPRICE,
            languagePreference: capitalizeFirstLetter(languageChoice),
          },
        },
        context: {
          headers: isAuthRecaptchaEnabled ? { 'x-recaptcha-token': recaptchaToken } : {},
        },
        onCompleted: () => {
          sendSubscribeToNewsletterEvent();
        },
      });
    } catch (e: unknown) {
      const gqlError = (e as ApolloError).graphQLErrors[0] as unknown as BffError;
      setRecaptchaError(gqlError.errorType);
    }
  };

  const onLanguageChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setLanguageChoice(value);
  };

  const onMarketingChoicesChange = ({ target: { name, checked } }: ChangeEvent<HTMLInputElement>) => {
    setMarketingChoices((prev) => ({
      ...prev,
      [name]: {
        key: (prev as MarketingChoice)[+name].key,
        optIn: checked,
      },
    }));
  };

  return {
    onLanguageChange,
    onMarketingChoicesChange,
    onSubmit,
    newsletterData,
    isCalled,
    checkboxError,
    error,
    recaptchaError,
    marketingPreferences,
    marketingChoices,
    locale,
    languageSelectionError,
    loading,
  };
};
